// Coaching philosophy content — timeless and program-agnostic.
// Edit the text here; the Philosophy page renders it automatically.

export const whyICoach = [
  "The longer I coached, the more I realized I wanted to become the mentor I wish I'd had growing up. I want to be intentional, available, and invested in helping athletes grow as players, leaders, and people.",
  "Winning matters, and so does losing. Both teach lessons if you're willing to learn from them. Long after the scores are forgotten, I hope the people I've coached remember how they were challenged, supported, and believed in.",
];

// Each card: title + ordered content blocks. Block types: "p" (paragraph) or
// "quote" (pull-quote, optional `author`).
export const philosophyCards = [
  {
    id: "faith",
    icon: "Cross",
    title: "Faith",
    belief: "An important part of my life that shapes the way I lead, serve, and build relationships.",
    preview: "It has taught me the importance of humility, accountability, and caring for people beyond basketball.",
    blocks: [
      {
        type: "p",
        text: "My faith in Jesus Christ is an important part of my life and shapes the way I lead, serve, and treat others.",
      },
      {
        type: "p",
        text: "In coaching, that means caring about each athlete beyond what they can do on the court. I want to challenge them, support them, and build relationships that help them grow as players and as people.",
      },
      {
        type: "p",
        text: "For me, coaching is an opportunity to serve others and make a positive impact through basketball.",
      },
    ],
  },
  {
    id: "relationships",
    icon: "Handshake",
    title: "Relationships",
    belief: "As I coach the player, I want to know the person.",
    preview: "Meaningful coaching begins with meaningful relationships.",
    blocks: [
      {
        type: "p",
        text: "Every athlete deserves to know they are valued beyond what they contribute on the court. The strongest relationships I've built with players have always made the hardest conversations possible. When athletes know you genuinely care about them as people, they become far more willing to accept coaching, embrace accountability, and trust you through adversity.",
      },
    ],
  },
  {
    id: "attitude",
    icon: "Flame",
    title: "Attitude",
    belief: "Character is revealed when adversity arrives.",
    preview: "You can't control every circumstance, but you can always control how you respond.",
    blocks: [
      { type: "p", text: "One question continues to shape the way I think about leadership and life:" },
      { type: "quote", text: "Where is your mindset when things aren't going your way?", author: "Jay Wright" },
      {
        type: "p",
        text: "Anyone can have a positive attitude when they're winning, playing well, or everything is going according to plan. Character is revealed when adversity arrives. I believe attitude isn't pretending everything is okay. It's acknowledging reality while choosing to respond with purpose.",
      },
      { type: "p", text: "As Jalen Brunson said:" },
      { type: "quote", text: "Go out there and do something about it.", author: "Jalen Brunson" },
      { type: "p", text: "We can't control every circumstance, but we can always control how we respond." },
    ],
  },
  {
    id: "standards",
    icon: "Target",
    title: "Standards",
    belief: "The standard should lead, not the mood.",
    preview: "Motivation fades. Standards remain.",
    blocks: [
      {
        type: "p",
        text: "I don't believe successful teams are built on motivation. Motivation comes and goes. Confidence rises and falls. Excitement eventually fades.",
      },
      { type: "p", text: "Standards remain." },
      { type: "p", text: "Standards shape habits. Habits shape culture. Culture shapes results." },
      { type: "p", text: "One of the sayings I live by is:" },
      { type: "quote", text: "We don't wait to rise to the occasion. We fall to the level of our standards." },
      {
        type: "p",
        text: "The original quote says, “We don’t rise to the occasion…” While I believe there’s truth in that, I intentionally added the word wait.",
      },
      {
        type: "p",
        text: "Life will inevitably present moments that demand more from us. There are times when we do have to rise to the occasion. The difference is that we don’t sit around waiting for those moments. We prepare every day through the standards we choose to uphold.",
      },
      {
        type: "p",
        text: "The goal isn't to be motivated every day. The goal is to build habits and standards that carry us forward long after motivation has disappeared.",
      },
    ],
  },
  {
    id: "servant-leadership",
    icon: "HeartHandshake",
    title: "Leadership",
    belief: "Leadership is both a responsibility and a reward.",
    preview: "It's about earning trust through your actions and making those around you better.",
    blocks: [
      {
        type: "p",
        text: "I believe leadership is both a responsibility and a reward. It's a responsibility because others are counting on you, and a reward because you're trusted to make a positive impact on the people around you. The best leaders aren't focused on recognition. They're focused on serving others, setting the standard, and helping the team move forward. Leadership isn't about having a title or being the loudest voice in the room. It's about earning trust through your actions and making those around you better every day. If no one notices the sacrifices you've made, you've still done your job.",
      },
    ],
  },
];

// Teaching — how I develop thinkers and problem-solvers.
export const teachingBelief = "Coaching is teaching.";
export const teachingPreview =
  "I want athletes to understand the game, not just memorize it.";
export const teachingIntro =
  "Coaching is teaching. My job isn't to just tell players what to do. I want them to understand the game well enough to think for themselves. Basketball moves too fast for me to control every possession, so I want players who can read what's happening, make a decision, and adjust on their own.";

export const teachingCards = [
  {
    id: "concepts",
    icon: "Lightbulb",
    title: "Teach Concepts, Not Memorization",
    lead: ["Plays matter. Concepts matter more."],
    body: "You can't run a set for every situation a game throws at you. So I teach the why behind what we do. When players understand why something works, they can adjust on the fly. I'd rather have players who can think than players who just run plays.",
  },
  {
    id: "questions",
    icon: "HelpCircle",
    title: "Ask Better Questions",
    lead: ["I ask questions before I give answers."],
    body: "It's quicker to just tell a kid what they did wrong, but the game doesn't stop for that. So I'll ask what they saw or what they'd do differently. I want them learning to coach themselves instead of always looking to the bench.",
  },
  {
    id: "adapt",
    icon: "Shuffle",
    title: "Adapt to the Athlete",
    lead: ["No two players learn the same way."],
    body: "Age, experience, confidence, and personality all change how someone picks things up. Good coaching isn't treating everyone exactly the same. The standard stays the same for everyone. How I teach it changes from player to player.",
  },
  {
    id: "failure",
    icon: "RefreshCw",
    title: "Redefining Failure",
    lead: ["Mistakes are expected. Refusing to learn from them isn't."],
    body: "Growth comes from making decisions, learning from the results, and adjusting.",
  },
];

// Sections still being written — rendered as elegant placeholders.
export const philosophyUpcoming = [
  { id: "excellence", num: "04", title: "Excellence", note: "In progress, being written." },
];
